import { existsSync } from "node:fs";
import { mkdir, readdir, readFile, writeFile } from "node:fs/promises";
import { join, resolve } from "node:path";

import { listAthenaAuthCanonicalMigrations } from "../auth/local/schema.ts";
import { ATHENA_MIGRATE_AUTH_SYNC_COMMAND } from "./commands.ts";
import {
  DEFAULT_MANAGED_AUTH_MIGRATIONS_DIRECTORY,
  DEFAULT_MIGRATIONS_DIRECTORY,
} from "./constants.ts";
import { ensureApplicationMigrationsDirectory } from "./ensure-directory.ts";

export const MANAGED_AUTH_MIGRATION_HEADER = [
  "-- AUTO-GENERATED BY @xylex-group/athena",
  "-- Embedded Athena Auth managed migration.",
  "-- Do not edit. The package-owned migration definition is authoritative.",
].join("\n");

export type ManagedAuthFileStatus =
  | "ok"
  | "missing"
  | "modified"
  | "unexpected";

export interface ManagedAuthMigrationArtifact {
  contents: string;
  filename: string;
  name: string;
  version: number;
}

export interface ManagedAuthFileInspection {
  filename: string;
  status: ManagedAuthFileStatus;
}

export interface ManagedAuthInspection {
  directory: string;
  drifted: boolean;
  files: ManagedAuthFileInspection[];
}

export interface MaterializeManagedAuthMigrationsOptions {
  cwd?: string;
  directory?: string;
  dryRun?: boolean;
  /** When true, overwrite locally edited managed files. Default true. */
  overwrite?: boolean;
}

export interface MaterializeManagedAuthMigrationsResult {
  directory: string;
  restored: string[];
  unchanged: string[];
  unexpected: string[];
  written: string[];
}

export interface EnsureAthenaProjectLayoutOptions {
  applicationDirectory?: string;
  cwd?: string;
  dryRun?: boolean;
  managedDirectory?: string;
}

export interface EnsureAthenaProjectLayoutResult {
  applicationDirectory: string;
  createdApplicationMigrations: boolean;
  createdManagedAuthMigrations: boolean;
  managedDirectory: string;
}

export function managedAuthMigrationFilename(name: string): string {
  return name.endsWith(".sql") ? name : `${name}.sql`;
}

export function renderManagedAuthMigrationFile(sql: string): string {
  const body = sql.trim();
  return `${MANAGED_AUTH_MIGRATION_HEADER}\n\n${body}\n`;
}

export function listManagedAuthMigrationArtifacts(): ManagedAuthMigrationArtifact[] {
  return listAthenaAuthCanonicalMigrations().map((entry) => ({
    contents: renderManagedAuthMigrationFile(entry.sql),
    filename: managedAuthMigrationFilename(entry.name),
    name: entry.name,
    version: entry.version,
  }));
}

export function resolveManagedAuthMigrationsDirectory(
  cwd: string,
  directory = DEFAULT_MANAGED_AUTH_MIGRATIONS_DIRECTORY
): string {
  return resolve(cwd, directory);
}

export async function ensureAthenaProjectLayout(
  options: EnsureAthenaProjectLayoutOptions = {}
): Promise<EnsureAthenaProjectLayoutResult> {
  const cwd = options.cwd ?? process.cwd();
  const applicationDirectory = resolve(
    cwd,
    options.applicationDirectory ?? DEFAULT_MIGRATIONS_DIRECTORY
  );
  const managedDirectory = resolveManagedAuthMigrationsDirectory(
    cwd,
    options.managedDirectory
  );
  const application = await ensureApplicationMigrationsDirectory({
    directory: applicationDirectory,
    dryRun: options.dryRun,
  });
  const managedMissing = !existsSync(managedDirectory);
  if (managedMissing && !options.dryRun) {
    await mkdir(managedDirectory, { recursive: true });
  }
  return {
    applicationDirectory,
    createdApplicationMigrations: application.created,
    createdManagedAuthMigrations: managedMissing,
    managedDirectory,
  };
}

function ignoreManagedName(name: string): boolean {
  return name.startsWith(".") || name === "README.md";
}

export async function inspectManagedAuthMigrations(
  options: MaterializeManagedAuthMigrationsOptions = {}
): Promise<ManagedAuthInspection> {
  const cwd = options.cwd ?? process.cwd();
  const directory = resolveManagedAuthMigrationsDirectory(
    cwd,
    options.directory
  );
  const expected = listManagedAuthMigrationArtifacts();
  const expectedByName = new Map(
    expected.map((item) => [item.filename, item.contents])
  );
  const files: ManagedAuthFileInspection[] = [];

  if (!existsSync(directory)) {
    for (const artifact of expected) {
      files.push({ filename: artifact.filename, status: "missing" });
    }
    return { directory, drifted: files.length > 0, files };
  }

  const listed = await readdir(directory);
  const seen = new Set<string>();
  for (const name of listed) {
    if (ignoreManagedName(name)) {
      continue;
    }
    seen.add(name);
    const expectedContents = expectedByName.get(name);
    if (expectedContents === undefined) {
      files.push({ filename: name, status: "unexpected" });
      continue;
    }
    const actual = await readFile(join(directory, name), "utf8");
    files.push({
      filename: name,
      status: actual === expectedContents ? "ok" : "modified",
    });
  }
  for (const artifact of expected) {
    if (!seen.has(artifact.filename)) {
      files.push({ filename: artifact.filename, status: "missing" });
    }
  }
  files.sort((left, right) => left.filename.localeCompare(right.filename));
  const drifted = files.some((file) => file.status !== "ok");
  return { directory, drifted, files };
}

export function formatManagedAuthDrift(
  inspection: ManagedAuthInspection
): string {
  const drifted = inspection.files.filter((file) => file.status !== "ok");
  if (drifted.length === 0) {
    return "";
  }
  const lines = [
    "Managed Embedded Auth migration files drifted from the package-owned definitions.",
    "Local files are a read-only view. Athena will not execute edited copies.",
    "",
    ...drifted.map((file) => `  ${file.status}: ${file.filename}`),
    "",
    "Restore with:",
    "",
    `  ${ATHENA_MIGRATE_AUTH_SYNC_COMMAND}`,
  ];
  return lines.join("\n");
}

export async function materializeManagedAuthMigrations(
  options: MaterializeManagedAuthMigrationsOptions = {}
): Promise<MaterializeManagedAuthMigrationsResult> {
  const cwd = options.cwd ?? process.cwd();
  const overwrite = options.overwrite !== false;
  const directory = resolveManagedAuthMigrationsDirectory(
    cwd,
    options.directory
  );
  const expected = listManagedAuthMigrationArtifacts();
  const written: string[] = [];
  const unchanged: string[] = [];
  const restored: string[] = [];
  const unexpected: string[] = [];

  if (!options.dryRun) {
    await mkdir(directory, { recursive: true });
  }

  const existing = existsSync(directory) ? await readdir(directory) : [];
  const existingSet = new Set(
    existing.filter((name) => !ignoreManagedName(name))
  );
  const expectedNames = new Set(expected.map((item) => item.filename));
  for (const name of existingSet) {
    if (!expectedNames.has(name)) {
      unexpected.push(name);
    }
  }

  for (const artifact of expected) {
    const path = join(directory, artifact.filename);
    const present = existingSet.has(artifact.filename);
    if (!present) {
      if (!options.dryRun) {
        await writeFile(path, artifact.contents, "utf8");
      }
      written.push(artifact.filename);
      continue;
    }
    const actual = await readFile(path, "utf8");
    if (actual === artifact.contents) {
      unchanged.push(artifact.filename);
      continue;
    }
    if (overwrite) {
      if (!options.dryRun) {
        await writeFile(path, artifact.contents, "utf8");
      }
      restored.push(artifact.filename);
    }
  }

  return { directory, restored, unchanged, unexpected, written };
}
